import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { ComparatorContext, useField } from '@kizi/comparator-core';
import {
  CoreTheme,
  Div,
  DivProps,
  Heading,
  Label,
  P,
} from '@kizi/core-components';
import { IconV2, withTooltip } from '@kizi/shared-components';

const TooltipIcon = withTooltip(IconV2);

const Flex = styled(Div)`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-top: 15px;
  gap: 15px 0;

  ${({ theme }) => {
    const breakpoints = (theme as CoreTheme).breakpoints;
    return breakpoints && breakpoints.length > 0
      ? `
    @media (min-width: ${breakpoints[0]}) {
      flex-direction: row;
      > div:not(:nth-child(1)) {
        margin-top: 0;
      }
    }`
      : '';
  }}
`;

const FlexItem = styled(Div)<any>`
  display: flex;
  flex-direction: column;
  min-width: 150px;
  padding: 15px 0px;

  ${({ comparator, wider, shorter }) => {
    let minWidth = '150px';
    if (wider) {
      minWidth = '192px';
    } else if (shorter) {
      minWidth = '125px';
    }
    return ['energy', 'energy-auction', 'car', 'triple-play'].includes(
      comparator,
    )
      ? `min-width: ${minWidth};`
      : '';
  }};
`;

const FlexItemValue = styled(P)`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;

interface Field {
  _label: string;
  _fieldName: string;
  _tooltip?: string;
  _value?: any;
  comparator?: string;
}
export interface DataSummaryProps extends DivProps {
  title: string;
  description: string;
  fields: Field[];
}

const FieldView = ({
  _fieldName,
  _tooltip,
  _label,
  _value,
  comparator,
}: Field) => {
  const field = useField(_fieldName);
  return (
    <FlexItem
      wider={[
        'Straatnaam',
        'Woonplaats',
        'Type verzekering',
        'Je auto',
      ].includes(_label)}
      shorter={['Huisnummer', 'Toevoeging', 'Postcode', 'Kenteken'].includes(
        _label,
      )}
      comparator={comparator}
    >
      <Label display="flex">
        {_label ?? field?.label}
        {_tooltip && (
          <TooltipIcon
            ml={1}
            position="relative"
            bottom="1px"
            name="info"
            size={18}
            css={{ fill: 'main.1' }}
            _tooltip={_tooltip}
            cursor="pointer"
          />
        )}
      </Label>
      <FlexItemValue variant="footnoteRegular">
        {_value ?? field?.value ?? '-'}
      </FlexItemValue>
    </FlexItem>
  );
};

export const DataSummary: React.FC<DataSummaryProps> = ({
  title,
  description,
  fields: incomingFields,
  ...restProps
}) => {
  const { name } = useContext(ComparatorContext);
  const fields = incomingFields.map((f) => (
    <FieldView {...f} key={f._label} comparator={name} />
  ));
  return (
    <Div {...restProps}>
      {title && (
        <Heading variant="H5" as="h5">
          {title}
        </Heading>
      )}
      {description && <Div>{description}</Div>}
      <Flex>{fields}</Flex>
    </Div>
  );
};
